import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, ArrowLeft, ChevronLeft, ChevronRight, Calendar as CalIcon, FileText, FileSpreadsheet, CheckSquare, Square, Lock, X } from "lucide-react";
import api, { getToken } from "@/lib/api";
import { toast } from "sonner";

function ymd(d) {
  return d.toISOString().slice(0, 10);
}

function parseYMD(s) {
  if (!s) return new Date();
  const [y, m, dd] = s.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m - 1, dd, 12, 0, 0);
}

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1, 12); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 12); }

function fmtMonth(d) {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function dotForDay(day) {
  if (!day || !day.summary || (day.summary.total_expected || 0) === 0) {
    return { color: "bg-slate-200", label: "No data" };
  }
  const ab = day.summary.absent || 0;
  if (ab === 0) return { color: "bg-emerald-500", label: "Full attendance" };
  if (ab <= 3) return { color: "bg-amber-400", label: `${ab} absent` };
  return { color: "bg-red-500", label: `${ab} absent` };
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("all");
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [hovered, setHovered] = useState(null);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpDate, setJumpDate] = useState(ymd(new Date()));

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  useEffect(() => {
    api.get("/seasons").then((r) => setSeasons(r.data.items || [])).catch(() => {});
  }, []);

  const loadMonth = async (monthCursor, sid) => {
    setLoading(true);
    try {
      const start = ymd(startOfMonth(monthCursor));
      const end = ymd(endOfMonth(monthCursor));
      const params = { start_date: start, end_date: end, page_size: 200 };
      if (sid && sid !== "all") params.season_id = sid;
      const r = await api.get("/archive/days", { params });
      setDays(r.data.items || []);
    } catch { toast.error("Failed to load calendar data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadMonth(cursor, seasonId); }, [cursor, seasonId]);

  const byDate = useMemo(() => Object.fromEntries(days.map((d) => [d.date, d])), [days]);

  const monthGrid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const startWeekday = first.getDay(); // 0=Sun
    const endWeekday = last.getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let dnum = 1; dnum <= last.getDate(); dnum++) {
      const d = new Date(cursor.getFullYear(), cursor.getMonth(), dnum, 12);
      cells.push({ date: ymd(d), dayOfMonth: dnum, weekday: d.getDay() });
    }
    for (let i = endWeekday; i < 6; i++) cells.push(null);
    return cells;
  }, [cursor]);

  const isToday = (ds) => ds === ymd(today);

  const onDayClick = (cell) => {
    if (!cell) return;
    const day = byDate[cell.date];
    if (selectMode) {
      if (!day) {
        toast.message("No data for that date — nothing to select");
        return;
      }
      setSelected((s) => ({ ...s, [cell.date]: !s[cell.date] }));
      return;
    }
    if (!day) {
      toast.message(`No attendance recorded for ${cell.date}`);
      return;
    }
    navigate(`/past-day?date=${cell.date}`);
  };

  const selectedDates = Object.entries(selected).filter(([, v]) => v).map(([k]) => k).sort();

  const exportSelected = async (kind) => {
    if (!selectedDates.length) return toast.message("Pick at least one day");
    const sd = selectedDates[0], ed = selectedDates[selectedDates.length - 1];
    try {
      const res = await api.get(`/export/range/${kind}`, { params: { start_date: sd, end_date: ed }, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `RRDC_Attendance_${sd}_to_${ed}.${kind === "csv" ? "csv" : "xlsx"}`;
      a.click();
    } catch { toast.error("Export failed"); }
  };

  const jumpToToday = () => setCursor(startOfMonth(new Date()));

  const goPrev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1, 12));
  const goNext = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1, 12));

  const jumpToSeasonStart = (s) => {
    const d = parseYMD(s.start_date);
    setCursor(startOfMonth(d));
    setSeasonId(s.id || s._id);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg text-slate-700 hover:bg-slate-100" data-testid="calendar-back">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-10 h-10 rounded-xl object-contain bg-white shadow-sm p-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River — Calendar</div>
              <h1 className="font-display text-lg font-bold text-slate-900">Attendance Calendar</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" data-testid="calendar-season-select">
              <option value="all">All Seasons</option>
              {seasons.map((s) => <option key={s.id || s._id} value={s.id || s._id}>{s.name} ({s.status})</option>)}
            </select>
            <button onClick={jumpToToday} className="btn-touch inline-flex items-center gap-2 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold" data-testid="jump-today">
              <CalIcon className="w-4 h-4" /> Today
            </button>
            <button onClick={() => setJumpOpen((v) => !v)} className="btn-touch inline-flex items-center gap-2 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold" data-testid="jump-date-btn">
              <CalIcon className="w-4 h-4" /> Jump to…
            </button>
          </div>
        </div>

        {/* Season jump links */}
        {seasons.length > 0 && (
          <div className="border-t border-slate-100 bg-slate-50/60">
            <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-2 flex items-center gap-2 flex-wrap text-xs">
              <span className="font-semibold text-slate-500 uppercase tracking-[0.18em]">Jump to:</span>
              {seasons.map((s) => (
                <button key={s.id || s._id} onClick={() => jumpToSeasonStart(s)} className="px-2.5 py-1 rounded-full bg-white border border-slate-200 hover:bg-blue-50 text-slate-700 font-medium" data-testid={`jump-${s.id || s._id}`}>
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button onClick={goPrev} className="btn-touch h-10 w-10 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 grid place-items-center text-slate-700" data-testid="month-prev"><ChevronLeft className="w-5 h-5" /></button>
            <h2 className="font-display text-2xl font-bold text-slate-900 min-w-[14ch] text-center" data-testid="current-month">{fmtMonth(cursor)}</h2>
            <button onClick={goNext} className="btn-touch h-10 w-10 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 grid place-items-center text-slate-700" data-testid="month-next"><ChevronRight className="w-5 h-5" /></button>
          </div>
          <button
            onClick={() => { setSelectMode((v) => !v); if (selectMode) setSelected({}); }}
            className={`btn-touch inline-flex items-center gap-2 px-4 rounded-xl font-semibold text-sm border ${selectMode ? "bg-blue-600 text-white border-blue-700" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            data-testid="toggle-select-mode"
          >
            {selectMode ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {selectMode ? "Done Selecting" : "Select Multiple Days"}
          </button>
        </div>

        {/* Grid */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-6">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="text-center text-[11px] uppercase tracking-[0.18em] text-slate-400 font-bold">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {monthGrid.map((cell, i) => {
              if (!cell) return <div key={i} className="aspect-square sm:aspect-[1.1] rounded-xl bg-slate-50/50" />;
              const day = byDate[cell.date];
              const isWeekend = cell.weekday === 0 || cell.weekday === 6;
              const noData = !day || (day.summary?.total_expected || 0) === 0;
              const isSel = selectMode && !!selected[cell.date];
              const todayCell = isToday(cell.date);
              const dot = dotForDay(day);
              return (
                <button
                  key={cell.date}
                  onClick={() => onDayClick(cell)}
                  onMouseEnter={() => setHovered(cell.date)}
                  onMouseLeave={() => setHovered((h) => (h === cell.date ? null : h))}
                  className={`relative aspect-square sm:aspect-[1.1] rounded-xl border text-left p-2 sm:p-3 transition group
                    ${isSel ? "bg-blue-50 border-blue-500 ring-2 ring-blue-300" : noData ? (isWeekend ? "bg-slate-50/70 border-slate-100 hover:bg-slate-100" : "bg-white border-slate-200 hover:bg-slate-50") : "bg-white border-slate-200 hover:border-blue-400 hover:shadow-sm"}
                    ${todayCell ? "ring-2 ring-blue-400 ring-offset-1" : ""}
                  `}
                  data-testid={`day-${cell.date}`}
                  title={
                    day
                      ? `${cell.date} — ${day.summary?.total_expected || 0} expected, ${day.summary?.present || 0} present, ${(day.summary?.late || 0) + (day.summary?.late_arrival || 0)} late, ${day.summary?.absent || 0} absent`
                      : `${cell.date} — no attendance recorded`
                  }
                >
                  <div className="flex items-start justify-between">
                    <span className={`font-display text-base sm:text-lg font-bold tabular-nums ${todayCell ? "text-blue-700" : isWeekend && noData ? "text-slate-400" : "text-slate-900"}`}>{cell.dayOfMonth}</span>
                    {day?.is_locked && <Lock className="w-3 h-3 text-emerald-600" data-testid={`lock-${cell.date}`} />}
                  </div>
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${dot.color}`} />
                    {day && day.summary && (day.summary.total_expected || 0) > 0 && (
                      <span className="text-[10px] tabular-nums text-slate-500 font-mono">
                        {day.summary.absent ? <span className="text-red-600 font-bold">{day.summary.absent}A</span> : <span className="text-emerald-600 font-bold">✓</span>}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Full attendance</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> 1-3 absent</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> 4+ absent</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-slate-200" /> No data</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3 text-emerald-600" /> Finalized / archived</span>
            {loading && <span className="text-slate-400 italic">Loading…</span>}
          </div>
        </div>
      </main>

      {/* Selection action bar */}
      {selectMode && selectedDates.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white border border-slate-200 shadow-2xl rounded-2xl px-4 py-3 flex items-center gap-3" data-testid="selection-bar">
          <span className="text-sm font-semibold text-slate-700">{selectedDates.length} day{selectedDates.length === 1 ? "" : "s"} selected</span>
          <span className="text-xs text-slate-400 font-mono">{selectedDates[0]} → {selectedDates[selectedDates.length - 1]}</span>
          <button onClick={() => exportSelected("csv")} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" data-testid="bulk-export-csv">
            <FileText className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => exportSelected("xlsx")} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold" data-testid="bulk-export-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setSelected({})} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold" data-testid="bulk-clear">
            <X className="w-4 h-4" /> Clear
          </button>
        </div>
      )}

      {/* Jump to date modal */}
      {jumpOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
            <h3 className="font-display text-lg font-bold mb-3">Jump to Date</h3>
            <input type="date" value={jumpDate} onChange={(e) => setJumpDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 mb-4 tabular-nums" data-testid="jump-date-input" />
            <div className="flex gap-2">
              <button onClick={() => setJumpOpen(false)} className="flex-1 btn-touch px-4 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold">Cancel</button>
              <button
                onClick={() => { if (jumpDate) { setCursor(startOfMonth(parseYMD(jumpDate))); setJumpOpen(false); } }}
                className="flex-1 btn-touch px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                data-testid="jump-date-confirm"
              >
                Go
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
